import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import type { Theme } from '@mui/material/styles';
import { Breadcrumbs, Card, CardContent, CardHeader, Grid, Link, Step, StepLabel, Stepper } from '@mui/material';
import { useCountriesStore, useNationalitiesStore, useProductsWithCurrenciesStore } from '@lib';
import { ApiUtils, ApplicationApi, ApplicationEntity, CustomerApi, ProductApi } from '@shared/api';
import { PermissionsUtils, RoutePaths, useRouteHook } from '@shared';
import {
  StepApplicant,
  StepCommunicationPreferences,
  StepContact,
  StepCurrency,
  StepEmployment,
  StepFinancialDetails,
  StepLifestyleManagement,
  StepPEP,
  StepPrivacyNotice,
  StepProduct,
  StepSecurity,
  StepSecurityQuestions,
} from './widgets';
import { DelayedLinearProgress, SnackbarContext, Button } from '@widgets';
import { IApplicationFormStep1, IApplicationFormStep2, TransformData } from './utils';

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    width: '100%',
  },
  button: {
    marginRight: theme.spacing(1),
  },
  completed: {
    display: 'inline-block',
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  dropArea: {
    textAlign: 'center',
    padding: theme.spacing(7),
  },
  saveIndicator: {
    left: 0,
    position: 'fixed',
    top: 0,
    width: '100%',
  },
}));

type Params = {
  selectedApplicationId?: string;
  customerId?: string;
};

export const ApplicationFormPage = () => {
  const { selectedApplicationId, customerId } = useParams<Params>();
  const { navigate } = useRouteHook();

  const { countries, getCountries } = useCountriesStore();
  const { nationalities, getNationalities } = useNationalitiesStore();
  const { loading: loadingProducts, products, getProducts } = useProductsWithCurrenciesStore();

  const [loadingApplication, setLoadingApplication] = useState<boolean>(true);
  const [applicationData, setApplicationData] = useState<ApplicationEntity>(TransformData.defaultApplication);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  const snackbar = useContext(SnackbarContext);
  const classes = useStyles();

  const methods = useForm<IApplicationFormStep1>({ defaultValues: TransformData.defaultApplicationFormStep1 });
  const methods2 = useForm<IApplicationFormStep2>({ defaultValues: TransformData.defaultApplicationFormStep2 });

  useEffect(() => {
    window.scrollTo(0, 0);
    getCountries();
    getNationalities();
    getProducts();
  }, []);

  useEffect(() => {
    if (selectedApplicationId) return;
    const security = PermissionsUtils.getUserAccountManager();
    setApplicationData((prev) => {
      const data = { ...prev, ...(security || {}) };
      methods.reset(TransformData.transformApplicationFormStep1(data));
      return data;
    });
  }, [selectedApplicationId]);

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      try {
        setLoadingApplication(true);
        const customerApplication = await CustomerApi.getCustomerApplicationDetails(customerId);
        setApplicationData((prev) => {
          const data = { ...prev, ...TransformData.transformCustomerApplication({ customerId, customerApplication }) };
          methods.reset(TransformData.transformApplicationFormStep1(data));
          methods2.reset(TransformData.transformApplicationFormStep2(data));
          return data;
        });
      } catch (error) {
        snackbar.error(ApiUtils.getError(error));
      } finally {
        setLoadingApplication(false);
      }
    })();
  }, [customerId]);

  useEffect(() => {
    (async () => {
      if (!selectedApplicationId) {
        setLoadingApplication(false);
        return;
      }

      if (selectedApplicationId === applicationData?.id.toString()) {
        setLoadingApplication(false);
        return;
      }

      try {
        setLoadingApplication(true);
        const application = await ApplicationApi.getMainApplication(selectedApplicationId);
        setApplicationData((prev) => {
          const data = { ...prev, ...application };
          methods.reset(TransformData.transformApplicationFormStep1(data));
          methods2.reset(TransformData.transformApplicationFormStep2(data));
          return data;
        });
      } catch (error) {
        snackbar.error(ApiUtils.getError(error));
      } finally {
        setLoadingApplication(false);
      }
    })();
  }, [selectedApplicationId, applicationData?.id]);

  const steps = [
    {
      title: 'Applicant Details',
      content: (
        <FormProvider {...methods}>
          <Grid container>
            <Grid item xs={12}>
              <Card className={classes.card}>
                <CardHeader title='Security' />
                <CardContent>
                  <StepSecurity />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <Card className={classes.card}>
                <CardHeader title='Product' />
                <CardContent>
                  <StepProduct loading={loadingProducts} products={products} />
                </CardContent>
              </Card>
            </Grid>
            {!loadingProducts && (
              <Grid item xs={12}>
                <Card className={classes.card}>
                  <CardHeader title='Currency and credit limit' />
                  <CardContent>
                    <StepCurrency products={products} />
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
          <Card className={classes.card}>
            <CardHeader title='Main card applicant details' />
            <CardContent>
              <StepApplicant countries={countries} nationalities={nationalities} />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Contact details' />
            <CardContent>
              <StepContact countries={countries} />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Employment details' />
            <CardContent>
              <StepEmployment />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='24/7 lifestyle management' />
            <CardContent>
              <StepLifestyleManagement />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Financial details' />
            <CardContent>
              <StepFinancialDetails countries={countries} />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Politically Exposed Persons (PEP) Declaration' />
            <CardContent>
              <StepPEP />
            </CardContent>
          </Card>
          <Card className={classes.card}>
            <CardHeader title='Security Question' />
            <CardContent>
              <StepSecurityQuestions />
            </CardContent>
          </Card>
        </FormProvider>
      ),
    },
    {
      title: 'Communication preferences',
      content: (
        <FormProvider {...methods2}>
          <Card className={classes.card}>
            <CardHeader title='Communication Preferences' />
            <StepCommunicationPreferences />
          </Card>
        </FormProvider>
      ),
    },
    {
      title: 'Privacy notice',
      content: (
        <Card className={classes.card}>
          <CardHeader title='Privacy Notice' />
          <StepPrivacyNotice />
        </Card>
      ),
    },
  ];

  const onSubmitFormStep1 = (data: IApplicationFormStep1) => {
    setApplicationData((prev) => ({ ...TransformData.transformFormToApplication({ ...prev, ...data }) }));
    setActiveStepIndex(activeStepIndex + 1);
    window.scrollTo(0, 0);
  };
  const onSubmitFormStep2 = (data: IApplicationFormStep2) => {
    setApplicationData((prev) => ({ ...TransformData.transformFormToApplication({ ...prev, ...data }) }));
    setActiveStepIndex(activeStepIndex + 1);
    window.scrollTo(0, 0);
  };
  const handleSave = async () => {
    try {
      setSaving(true);
      await getProductBeforeSave(applicationData);
    } catch (error) {
      snackbar.error(ApiUtils.getError(error));
    } finally {
      setSaving(false);
    }
  };
  const handleBack = () => {
    setActiveStepIndex(activeStepIndex - 1);
    window.scrollTo(0, 0);
  };
  const handleValidate = async () => {
    // @ts-ignore
    const isValidStep1 = (await methods.trigger()) && !methods.formState.errors.pepGroup?.message;
    const isValidStep2 = methods2.getValues('acceptedTC');

    if (isValidStep1 && isValidStep2) snackbar.success('Application form is valid');
    else if (!isValidStep2) snackbar.error('You should accept Terms and Conditions');

    return isValidStep1 && isValidStep2;
  };

  const saveApplication = async (applicationData: ApplicationEntity, submit?: boolean) => {
    try {
      if (submit) setProcessing(true);
      const applicationId: string = await ApplicationApi.saveApplication(applicationData);
      if (submit) {
        await ApplicationApi.submitApplication(applicationId);
        snackbar.success('Application successfully submitted');
      } else {
        snackbar.success(selectedApplicationId ? 'Application successfully updated' : 'Application successfully created');
      }
      navigate(RoutePaths.applicationsDetails.onNavigate(applicationId));
    } catch (error) {
      snackbar.error(ApiUtils.getError(error));
    } finally {
      setSaving(false);
      setProcessing(false);
    }
  };

  const getProductBeforeSave = async (applicationData: ApplicationEntity, submit?: boolean) => {
    try {
      if (submit) setProcessing(true);
      const products = await ProductApi.getProducts();
      const matchedProduct = products.find((product) => product.name === applicationData.productName);
      const simpleProductId = matchedProduct?.simpleProductId || applicationData.simpleProductId || '';
      const newData = { ...applicationData, simpleProductId };
      await saveApplication(newData, submit);
      setApplicationData(newData);
    } catch (error) {
      setProcessing(false);
      throw error;
    }
  };

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='hover' color='inherit' href={RoutePaths.applications.onNavigate()}>
          Applications
        </Link>
        {selectedApplicationId && (
          <Link
            sx={{ textTransform: 'uppercase', fontSize: '12px' }}
            underline='hover'
            color='inherit'
            href={RoutePaths.applicationsDetails.onNavigate(applicationData.id)}
          >
            {applicationData.name || 'Loading...'}
          </Link>
        )}
        {selectedApplicationId && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            Edit
          </Link>
        )}
        {!selectedApplicationId && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            New Application
          </Link>
        )}
      </Breadcrumbs>
      {applicationData && (
        <Grid container alignContent='center'>
          <DelayedLinearProgress className={classes.saveIndicator} loading={saving} />
          <Grid item xs={12}>
            <DelayedLinearProgress loading={loadingApplication} />
            <Stepper nonLinear activeStep={activeStepIndex}>
              {steps.map((step, idx) => (
                <Step key={idx} completed={idx < activeStepIndex}>
                  <StepLabel>{step.title}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
          <Grid item xs={12}>
            <form onSubmit={activeStepIndex === 0 ? methods.handleSubmit(onSubmitFormStep1) : methods2.handleSubmit(onSubmitFormStep2)}>
              {steps[activeStepIndex]?.content ?? null}
              <div>
                <Button className={classes.button} type='button' disabled={activeStepIndex === 0 || saving} onClick={handleBack}>
                  Back
                </Button>
                {activeStepIndex >= steps.length - 1 && (
                  <Button className={classes.button} variant='contained' color='primary' type='button' loading={saving} onClick={handleSave}>
                    Save
                  </Button>
                )}
                {activeStepIndex < steps.length - 1 && (
                  <Button className={classes.button} variant='contained' color='primary' type='submit' loading={processing}>
                    Next
                  </Button>
                )}
                <Button className={classes.button} variant='contained' color='primary' type='button' onClick={handleValidate}>
                  Validate
                </Button>
              </div>
            </form>
          </Grid>
        </Grid>
      )}
    </>
  );
};
